import React from 'react';
import _ from 'lodash';
import { Button, Box, Stack, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitProductAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { vatRates } from '../../../../../services/constants';
import { recalculate } from '../../../order/order-details/tabs/SettlementTab/documents/recalculate';

export const TemplateProductForm = props => {
  const { data, submitCallback, handleClose } = props;

  const { loading, mutate } = useMutation(submitProductAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    quantity: Yup.number().typeError('Należy podać liczbę całkowitą')
      .required('To pole nie może być puste'),
    gross_price: Yup.number().typeError('Należy podać liczbę dziesiętną')
      .required('To pole nie może być puste'),
    net_price: Yup.number().typeError('Należy podać liczbę dziesiętną')
      .required('To pole nie może być puste')
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    quantity: data ? data.quantity || '1' : '1',
    vat_rate: data ? data.vat_rate || '23' : '23',
    unit: data ? data.unit || '' : '',
    pkwiu: data ? data.pkwiu || '' : '',
    gross_price: data ? data.gross_price || '0' : '0',
    net_price: data ? data.net_price || '0' : '0',
    vat_amount: data ? data.vat_amount || '0' : '0',
    gross_value: data ? data.gross_value || '0' : '0',
    net_value: data ? data.net_value || '0' : '0',
    template: true
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano szablon' : 'Dodano nowy szablon', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego szablonu');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto szablon ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć szablonu ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validateOnChange: true,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  const transform = val => val && (!isNaN(val) || !isNaN(parseFloat(val))) ? val.toString().replace(',', '.') : val;

  const recalculateSignal = (name, value) => {
    const { newQuantity, newGrossPrice, newNetPrice, newNetValue, newVatRate, newVatAmount, newGrossValue } =
      recalculate(name, transform(value), transform(formik.values.quantity),
        transform(formik.values.gross_price), transform(formik.values.net_price), transform(formik.values.net_value),
        formik.values.vat_rate, transform(formik.values.vat_amount), transform(formik.values.gross_value));
    if (newQuantity !== formik.values.quantity) {
      formik.setFieldValue('quantity', newQuantity);
    }
    if (newGrossPrice !== formik.values.gross_price) {
      formik.setFieldValue('gross_price', newGrossPrice);
    }
    if (newNetPrice !== formik.values.net_price) {
      formik.setFieldValue('net_price', newNetPrice);
    }
    if (newNetValue !== formik.values.net_value) {
      formik.setFieldValue('net_value', newNetValue);
    }
    console.log(newVatRate);
    if (newVatRate != formik.values.vat_rate) {
      formik.setFieldValue('vat_rate', newVatRate);
    }
    if (newVatAmount !== formik.values.vat_amount) {
      formik.setFieldValue('vat_amount', newVatAmount);
    }
    if (newGrossValue !== formik.values.gross_value) {
      formik.setFieldValue('gross_value', newGrossValue);
    }
  };

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa produktu"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ilość</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="quantity"
              id="quantity"
              type="text"
              placeholder="ilość"
              value={formik.values.quantity}
              onChange={e => {
                formik.setFieldValue('quantity', e.target.value);
                recalculateSignal('quantity', e.target.value);
              }}
              error={formik.touched.quantity && Boolean(formik.errors.quantity)}
              helperText={formik.touched.quantity && formik.errors.quantity}
              debounceTimeout={200}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">J.m.</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="unit"
              id="unit"
              type="text"
              placeholder="j.m."
              value={formik.values.unit}
              onChange={formik.handleChange}
              error={formik.touched.unit && Boolean(formik.errors.unit)}
              helperText={formik.touched.unit && formik.errors.unit}
              debounceTimeout={100}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">CN / PKWIU</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="pkwiu"
              id="pkwiu"
              type="text"
              placeholder="cn / pkwiu"
              value={formik.values.pkwiu}
              onChange={formik.handleChange}
              error={formik.touched.pkwiu && Boolean(formik.errors.pkwiu)}
              helperText={formik.touched.pkwiu && formik.errors.pkwiu}
              debounceTimeout={100}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Cena brutto [PLN]</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="gross_price"
              id="gross_price"
              type="text"
              placeholder="cena brutto"
              value={formik.values.gross_price}
              onChange={e => {
                formik.setFieldValue('gross_price', e.target.value);
                recalculateSignal('gross_price', e.target.value);
              }}
              error={formik.touched.gross_price && Boolean(formik.errors.gross_price)}
              helperText={formik.touched.gross_price && formik.errors.gross_price}
              debounceTimeout={200}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Cena netto [PLN]</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="net_price"
              id="net_price"
              type="text"
              placeholder="cena brutto"
              value={formik.values.net_price}
              onChange={e => {
                formik.setFieldValue('net_price', e.target.value);
                recalculateSignal('net_price', e.target.value);
              }}
              error={formik.touched.net_price && Boolean(formik.errors.net_price)}
              helperText={formik.touched.net_price && formik.errors.net_price}
              debounceTimeout={200}
              element={SmallFormTextField}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Stawka VAT</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="vat_rate"
              name="vat_rate"
              options={vatRates}
              value={formik.values.vat_rate}
              onChange={e => {
                formik.setFieldValue('vat_rate', e.target.value);
                recalculateSignal('vat_rate', e.target.value);
              }}
              error={formik.touched.vat_rate && Boolean(formik.errors.vat_rate)}
              helperText={formik.touched.vat_rate && formik.errors.vat_rate}
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Wartość netto [PLN]</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              name="net_value"
              id="net_value"
              type="text"
              value={formik.values.net_value}
              disabled
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Wartość VAT [PLN]</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              name="vat_amount"
              id="vat_amount"
              type="text"
              value={formik.values.vat_amount}
              disabled
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Wartość brutto [PLN]</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              name="gross_value"
              id="gross_value"
              type="text"
              value={formik.values.gross_value}
              disabled
            />
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć Szablon?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
      >
        Czy na pewno chcesz usunąć ten szablon?
      </ConfirmDialog>
    </>

  );
};
